import { Modal, Spinner, TextInput, TextInputProps } from "flowbite-react"
import moment from "moment"
import React, { useState, forwardRef } from "react"
import DatePicker, { ReactDatePickerProps } from "react-datepicker"

import RightArrow from "@icons/RightArrow"
import { toLocalDate } from "@mono/essentials/utils/Common"
import { dateFormatWithoutTimezone } from "@mono/essentials/utils/Common"
import modalTheme from "@theme/components/modal"
import textInputTheme from "@theme/components/textInput"

import MonoInlineDatePicker from "../DatePickerInline/MonoInlineDatePicker"

import styles from "./MonoDatePicker.module.css"

interface MonoDatePickerProps extends Omit<ReactDatePickerProps, "selectsRange"> {
  inputFieldProps?: TextInputProps
  loading?: boolean
  showSpinner?: boolean
  customTextInput?: any
  enableOnBlur?: boolean
  isFromWidget?: boolean
  headerContent?: React.ReactNode
}

const MonoDatePicker = ({
  inputFieldProps,
  monthsShown = 1,
  onChange,
  headerContent,
  popperClassName,
  loading = false,
  value,
  enableOnBlur = true,
  showSpinner = false,
  customTextInput = null,
  isFromWidget = false,
  onMonthChange,
  ...rest
}: MonoDatePickerProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    rest?.selected ? moment(rest?.selected, "DD-MM-YY").toDate() : toLocalDate(moment())
  )

  const [isModalOpen, setIsModalOpen] = useState(false)

  const onValueChange = (
    date: Date | null,
    event: React.SyntheticEvent<any, Event> | undefined
  ) => {
    setSelectedDate(date)
    onChange?.(dateFormatWithoutTimezone(date), event)
  }

  const isSmallScreen = () => {
    return window.innerWidth <= 576
  }

  let CustomInput = null
  if (!customTextInput) {
    // eslint-disable-next-line react/display-name
    CustomInput = forwardRef((props, ref: React.Ref<HTMLInputElement>) => {
      return (
        <TextInput
          {...inputFieldProps}
          autoComplete={"off"}
          onClick={(props as unknown as any).onClick}
          onBlur={() => {
            if (enableOnBlur) {
              setSelectedDate(null)
            }
          }}
          theme={
            isFromWidget
              ? {
                  field: {
                    input: {
                      colors: {
                        gray: `${textInputTheme?.textInput?.field?.input?.colors?.gray} !border-neutral-450`,
                      },
                    },
                  },
                }
              : {}
          }
          ref={ref}
          readOnly
        />
      )
    })
  } else {
    CustomInput = customTextInput
  }

  const renderDatePicker = () => {
    if (isSmallScreen() && isFromWidget) {
      return (
        <div>
          <CustomInput
            onClick={() => {
              setIsModalOpen(true)
              rest?.onCalendarOpen()
            }}
          />
          <Modal
            dismissible
            size={"sm"}
            show={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            theme={{
              root: { sizes: { sm: `${modalTheme.modal.root.sizes} !max-w-fit` } },
            }}
          >
            <Modal.Body className={"!p-0"}>
              <MonoInlineDatePicker
                {...rest}
                inline
                onChange={(date, event) => {
                  onValueChange(date, event)
                  setIsModalOpen(false)
                }}
                loading={loading}
                showSpinner={showSpinner}
                onMonthChange={onMonthChange}
                headerContent={headerContent}
                isSelectedDate={selectedDate}
                popperClassName={`${styles.inlineDatePicker} lg:px-4 md:px-2 px-4 pt-4 pb-3 !border-gray-100 shadow !inline-grid md:w-full md:!w-auto`}
                containerClassName={`md:inline !w-auto flex justify-center`}
              />
            </Modal.Body>
          </Modal>
        </div>
      )
    }
    return (
      <DatePicker
        {...rest}
        popperClassName={`${styles.datePicker} ${popperClassName ?? ""} !z-10`}
        selected={selectedDate}
        customInput={<CustomInput />}
        onChange={onValueChange}
        onMonthChange={onMonthChange}
        renderCustomHeader={({ decreaseMonth, increaseMonth, monthDate }) => {
          return (
            <>
              {loading && showSpinner ? (
                <Spinner size={"md"} light className={"absolute left-[46%] top-[160%] z-1"} />
              ) : null}
              {headerContent}
              <div className={"flex flex-row items-start justify-between gap-1 p-0.5"}>
                <RightArrow
                  onClick={decreaseMonth}
                  width={20}
                  height={20}
                  className={"rotate-180 transform cursor-pointer text-neutral-800"}
                />
                <p className={"text12Semibold text-neutral-800"}>
                  {moment(monthDate).format("MMMM YYYY")}
                </p>
                <RightArrow
                  onClick={increaseMonth}
                  width={20}
                  height={20}
                  className={"cursor-pointer text-neutral-800"}
                />
              </div>
            </>
          )
        }}
        peekNextMonth={false}
      />
    )
  }

  return (
    <div className={`w-full ${loading ? styles.loading : ""}`}>
      <link
        rel={"stylesheet"}
        href={
          "https://cdnjs.cloudflare.com/ajax/libs/react-datepicker/4.10.0/react-datepicker.min.css"
        }
      />
      {renderDatePicker()}
    </div>
  )
}

export default MonoDatePicker
